import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    untracked,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { map as radashMap } from 'radash';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { EntityAttributesComponent } from '@connect-services/client/components/entity-attributes/entity-attributes.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { AttributeValue } from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import {
    CanDeactivateComponent,
    CanDeactivateDialogConfig,
} from '@connect-services/client/guards/can-deactivate.guard';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import { ButtonComponent, ButtonType } from '@connect-services/client/lib/components/button/button.component';
import { SelectComponent, SelectOption } from '@connect-services/client/lib/components/select/select.component';
import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositsService,
    DepositWithFolderAndStandard,
    EditAction,
    EntityState,
    Layer,
    LayerAttribute,
    LayersService,
    LayerWithAttributes,
    NonEditableColumns,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeInformationService } from '@connect-services/client/services/attribute-information.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import {
    CreationContext,
    CreationSourceEnum,
    EntityDetailCreationContextService,
} from '@connect-services/client/services/entity-detail-creation-context.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { excludedAttributes } from '@connect-services/client/services/layer.service';
import { MapCreationGeomService } from '@connect-services/client/services/map-creation-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        EntityAttributesComponent,
        ExpansionPanelComponent,
        InputTextComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-entity-detail-create-edit',
    templateUrl: './entity-detail-create-edit.component.html',
})
export class EntityDetailCreateEditComponent implements CanDeactivateComponent, OnInit {
    protected readonly bookmarkedAttributes = signal<AttributeValue[]>([]);
    protected readonly canSaveCurrentCreation = computed(() => {
        if (this.editEntityDetailService.recomputeError() || this.isCancelCreation()) {
            return false;
        }

        if (!this.entityDetailCreationContextService.shouldAutoSave() || this.hasSaved() || !this.hasValidForms()) {
            return false;
        }

        if (!this.entityDetailCreationContextService.isEditingExistingCreatedEntity()) {
            return true;
        }

        return (
            !!this.editEntityDetailService.bookmarkedAttributesForm()?.dirty ||
            !!this.editEntityDetailService.notBookmarkedAttributesForm()?.dirty
        );
    });
    protected readonly columnNamesWithReference = signal<string[]>([]);
    protected readonly currentBusinessIdentifierControl = computed<FormControl<unknown> | null>(() => {
        const businessIdentifierKey = this.currentBusinessIdentifierKey();
        if (!businessIdentifierKey) {
            return null;
        }

        return this.editEntityDetailService.getAttributeFormControl(businessIdentifierKey);
    });
    protected readonly currentBusinessIdentifierKey = computed<string | null>(() => {
        const businessIdentifierKey = this.editEntityDetailService.businessIdentifierKey();
        if (!businessIdentifierKey || businessIdentifierKey === 'uuid') {
            return null;
        }

        return businessIdentifierKey;
    });
    protected readonly currentBusinessIdentifierTitlePlaceholder = computed<string>(() => {
        const control = this.currentBusinessIdentifierControl();
        const value = control?.value;

        if (value === null || value === undefined) {
            return '';
        }

        return String(value);
    });
    protected readonly defaultOptions = signal<SelectOption<DepositWithFolderAndStandard>[]>([]);
    protected readonly depositOptions = signal<SelectOption<DepositWithFolderAndStandard>[]>([]);
    protected readonly isCancelCreation = signal<boolean>(false);
    protected readonly isFromStandardizeDeposit = signal<boolean>(false);
    protected readonly isLoading = signal<boolean>(false);
    protected readonly nonEditableColumns = signal<NonEditableColumns[]>([]);
    protected readonly nonEditableColumnsName = computed<string[]>(() =>
        this.nonEditableColumns().map((nonEditableColumn: NonEditableColumns) => nonEditableColumn.name),
    );
    protected readonly nonEditableColumnsReasonByName = computed<Record<string, string | null>>(() =>
        this.nonEditableColumns().reduce(
            (accumulator: Record<string, string | null>, nonEditableColumn: NonEditableColumns) => {
                return { ...accumulator, [nonEditableColumn.name]: nonEditableColumn.reasonForUnavailability };
            },
            {},
        ),
    );
    protected readonly notBookmarkedAttributes = signal<AttributeValue[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    protected readonly mapCreationGeomService = inject(MapCreationGeomService);

    private creationContextSnapshot: {
        depositIds: string[];
        entityId: string | null;
        layerId: string | null;
        source: CreationSourceEnum | null;
    } = {
        depositIds: [],
        entityId: null,
        layerId: null,
        source: null,
    };
    private readonly hasSaved = signal(false);
    private readonly isFromSourceLayer = computed<boolean>(
        () =>
            !!this.mapCreationGeomService.sourceLayerId() &&
            !!this.mapCreationGeomService.sourceAttributes() &&
            !!this.mapCreationGeomService.sourceTableName,
    );
    private lastHandledCreationContextKey: string | null = null;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly attributeInformationService = inject(AttributeInformationService);
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly depositsService = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly homeService = inject(HomeService);
    private readonly layersService = inject(LayersService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly navigationStateService = inject(NavigationStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.entityDetailCreationContextService.clearCurrentCreationFinalizer(this.currentCreationFinalizer);

            if (this.isFromSourceLayer()) {
                this.mapEditModeService.activeEditLayerIds.set(new Set());
            }

            this.mapCreationGeomService.resetCreationEntityRelatedData();

            if (this.entityDetailCreationContextService.isEditingExistingCreatedEntity()) {
                this.entityDetailCreationContextService.reset();
            }
        });

        effect(async () => {
            const entityId = this.entityDetailCreationContextService.entityId();
            const layer = this.getCurrentLayer();

            if (!layer || !entityId) {
                return;
            }

            this.mapCreationGeomService.layerAttributes = [];
            await this.setLayerDataAttributes(entityId);
        });

        effect(() => {
            const context: CreationContext = this.getCreationContext();
            this.entityDetailCreationContextService.isEditingExistingCreatedEntity();

            untracked(() => {
                this.handleCreationContextRefresh(context);
            });
        });
    }

    public canDeactivateComponent(): boolean {
        if (this.entityDetailCreationContextService.isEditingExistingCreatedEntity() && !this.hasDirtyForms()) {
            return true;
        }

        return this.mapCreationGeomService.canLeaveCreationPanel();
    }

    public confirmDeactivateComponent(): void {
        this.isCancelCreation.set(true);
        this.mapCreationGeomService.canLeaveCreationPanel.set(false);
    }

    public getComponentDeactivateDialogConfig(): CanDeactivateDialogConfig {
        return {
            confirmLabel: this.translocoService.translate('quit'),
            description: this.translocoService.translate('editMode.entityNotSavedLeaveCreation'),
            type: ModalType.DANGER,
            width: '36rem',
        };
    }

    public async ngOnInit(): Promise<void> {
        this.hasSaved.set(false);
        this.editEntityDetailService.isEditMode.set(true);

        this.synchronizeCreationContextSnapshot(this.getCreationContext());
        this.entityDetailCreationContextService.registerCurrentCreationFinalizer(this.currentCreationFinalizer);
        this.editEntityDetailService.resetFormsValues();

        if (this.entityDetailCreationContextService.isMapCreation()) {
            await this.initializeFromMapContext();
        } else {
            await this.initializeFromTabularContext();
        }

        const selectedDeposit = this.mapCreationGeomService.selectedDeposit();
        this.isFromStandardizeDeposit.set(!!selectedDeposit?.standard);

        const entityId = this.entityDetailCreationContextService.entityId();
        if (entityId) {
            await this.setLayerDataAttributes(entityId);
        }
        await this.fetchColumnNamesWithReferences(this.editEntityDetailService.layer());
        if (this.entityDetailCreationContextService.isEditingExistingCreatedEntity()) {
            return;
        }

        const success = await this.recompute(
            null,
            false,
            this.mapCreationGeomService.sourceAttributes(),
            this.mapCreationGeomService.sourceTableName(),
        );

        if (success) {
            return;
        }

        this.snackBarService.openErrorSnackBar('editMode.recomputeErrorCorrectError');
    }

    protected async cancelCreation(): Promise<void> {
        const projectId = this.projectBloc.value()?.id;
        const entityId = this.entityDetailCreationContextService.entityId();
        if (!projectId || !entityId) {
            return;
        }

        this.mapEditModeService.stopLayerEditMode();

        this.isCancelCreation.set(true);
        this.mapCreationGeomService.canLeaveCreationPanel.set(true);

        this.entityDetailCreationContextService.disableAutoSave();
        this.editEntityDetailService.resetFormsValues();

        await this.closeEntityDetailPanel();

        this.closeTabularCreation();
    }

    protected async fetchColumnNamesWithReferences(layer?: Layer | null): Promise<void> {
        const selectedDeposit = this.mapCreationGeomService.selectedDeposit();
        const selectedLayer = layer ?? this.mapCreationGeomService.selectedLayer();

        if (!selectedDeposit || !selectedLayer?.tableName) {
            return;
        }

        this.columnNamesWithReference.set(
            await this.editEntityDetailService.getColumnNamesWithReferences(selectedDeposit, selectedLayer.tableName),
        );

        this.nonEditableColumns.set(
            await this.editEntityDetailService.getNonEditableColumns(selectedDeposit, selectedLayer.tableName),
        );

        const depositIdControl = this.editEntityDetailService.notBookmarkedAttributesForm()?.get('deposit_id');
        depositIdControl?.setValue(selectedDeposit.id);
    }

    protected getCurrentLayer(): Layer | null {
        const mapLayer: Layer | null = this.mapCreationGeomService.selectedLayer();
        if (mapLayer) {
            return mapLayer;
        }

        const contextLayerIds: string[] = this.entityDetailCreationContextService.layerIds();
        if (!contextLayerIds.length) {
            return null;
        }

        return this.projectEntityTableService.layers().find((layer) => layer.id === contextLayerIds[0]) ?? null;
    }

    protected hasCurrentDeposit(): boolean {
        const contextDepositIds: string[] = this.entityDetailCreationContextService.depositIds();
        if (!contextDepositIds.length) {
            return !!this.mapCreationGeomService.selectedDeposit();
        }

        return this.depositOptions().some((option: SelectOption<DepositWithFolderAndStandard>) =>
            contextDepositIds.includes(option.value.id),
        );
    }

    protected recompute(
        formEditActionEvent: EditEventAction | null,
        saveExtraObject: boolean = false,
        sourceAttributes: Record<string, unknown> | null = null,
        sourceTableName: string | null = null,
    ): Promise<boolean> {
        const selectedDeposit = this.mapCreationGeomService.selectedDeposit();
        if (!selectedDeposit?.standard) {
            if (saveExtraObject) {
                const projectId = this.projectBloc.value()?.id;

                if (formEditActionEvent && projectId) {
                    this.editModeStateService.addEdit(projectId, { actions: [formEditActionEvent] });
                } else {
                    this.addNotStandardizedCreationEdit();
                }
            }

            return Promise.resolve(true);
        }

        return this.editEntityDetailService.recomputeEntityProperties(
            this.mapCreationGeomService.getCreatedGeom(),
            EditAction.CREATE,
            formEditActionEvent,
            true,
            saveExtraObject,
            sourceAttributes,
            sourceTableName,
        );
    }

    protected async selectDeposit(deposits: DepositWithFolderAndStandard[]): Promise<void> {
        const selectedDeposit = deposits[0];
        if (!selectedDeposit) {
            return;
        }

        this.mapCreationGeomService.selectedDeposit.set(selectedDeposit);

        const currentLayer: Layer | null = this.getCurrentLayer();
        if (!currentLayer) {
            return;
        }

        const selectedLayer: Layer | undefined = this.findLayerForDeposit(currentLayer, selectedDeposit.id);

        const entityId: string | null = this.entityDetailCreationContextService.entityId();
        if (!entityId || !selectedLayer) {
            return;
        }

        await this.projectEntityTableService.openCreateEntityPanel(selectedLayer.id, selectedLayer.name, {
            existingEntityId: entityId,
            haveToFinalizeCreation: false,
        });

        await this.fetchColumnNamesWithReferences(selectedLayer);

        if (this.contentLayoutService.isDataTableView()) {
            await this.recompute(null);

            return;
        }

        this.mapCreationGeomService.redrawCustomLayer();
    }

    protected setAttributes(bookmarked: AttributeValue[], notBookmarked: AttributeValue[]): void {
        const sortedBookmarked = bookmarked.toSorted((a, b) => (a.order || 0) - (b.order || 0));
        const sortedNotBookmarked = notBookmarked.toSorted((a, b) => a.key.localeCompare(b.key));

        this.bookmarkedAttributes.set(sortedBookmarked);
        this.notBookmarkedAttributes.set(sortedNotBookmarked);
    }

    protected updateCurrentBusinessIdentifierFromTitle(value: string): void {
        const control = this.currentBusinessIdentifierControl();
        if (!control) {
            return;
        }

        control.setValue(value);
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity({
            emitEvent: true,
        });
    }

    protected async validateEntity(isValidateFromPanelClose: boolean = false): Promise<void> {
        if (this.editEntityDetailService.areFormsOnError() || this.hasSaved() || !this.hasValidForms()) {
            return;
        }

        const attributes: Record<string, unknown> = this.getFormAttributes();

        let formEditEvent: EditEventAction | null;
        if (this.isSnapshotMapCreation()) {
            formEditEvent = this.getMapEntityEditAction(attributes);
        } else {
            formEditEvent = this.getTabularEntityEditAction(attributes);
        }
        this.hasSaved.set(true);

        const success: boolean = await this.recompute(formEditEvent, true);

        if (!success) {
            this.hasSaved.set(false);

            return;
        }

        this.editEntityDetailService.markAttributeFormsAsPristine();
        this.mapCreationGeomService.canLeaveCreationPanel.set(true);

        this.snackBarService.openSuccessSnackBar('editMode.creation.entitySaved');

        if (isValidateFromPanelClose) {
            return;
        }

        const wasMapCreation: boolean = this.entityDetailCreationContextService.isMapCreation();

        const shouldRedrawCreatedEntity: boolean =
            wasMapCreation && !this.mapCreationGeomService.selectedDeposit()?.standard && this.isSnapshotMapCreation();

        this.projectEntityTableService.closeEntityDetailPanel();
        await this.closeEntityDetailPanel();

        if (!wasMapCreation) {
            this.mapEditModeService.stopLayerEditMode();
            this.closeTabularCreation();

            return;
        }

        if (this.isFromSourceLayer()) {
            this.mapEditModeService.stopLayerEditMode();
            this.mapCreationGeomService.stopCreationMode();

            return;
        }

        if (shouldRedrawCreatedEntity) {
            await this.mapSourceService.drawSources(this.sourcesService.sources(), { fitBounds: false }, true);
        }

        const shouldKeepAddEntityMode: boolean =
            wasMapCreation && !this.isFromSourceLayer() && this.mapEditModeService.isAddEntityModeActive();

        this.closeMapCreationAfterSaveTrigger(shouldKeepAddEntityMode);

        if (shouldKeepAddEntityMode) {
            await this.mapCreationGeomService.getAdditionType();
        }
    }

    private addNotStandardizedCreationEdit(): void {
        const project = this.projectBloc.value();
        const layer = this.editEntityDetailService.layer();
        if (!project || !layer?.tableName) {
            return;
        }

        const attributes: { [key: string]: unknown } = {
            ...this.editEntityDetailService.bookmarkedAttributesForm()?.value,
            ...this.editEntityDetailService.notBookmarkedAttributesForm()?.value,
            geom: this.mapCreationGeomService.getCreatedGeom(),
            uuid: this.editEntityDetailService.entityId(),
        };
        const businessIdentifierKey = this.editEntityDetailService.businessIdentifierKey() ?? 'uuid';

        const eventAction: EditEventAction = {
            action: EditAction.CREATE,
            after: attributes,
            before: null,
            businessIdentifier: { key: businessIdentifierKey, value: attributes[businessIdentifierKey] },
            entityId: this.editEntityDetailService.entityId(),
            layerId: layer.id,
            tableName: layer.tableName,
        };
        this.editModeStateService.addEdit(project.id, { actions: [eventAction] });
    }

    private buildAttributeFormState(filteredAttributes: LayerAttribute[]): {
        bookmarkedAttributes: AttributeValue[];
        notBookmarkedAttributes: AttributeValue[];
    } {
        return filteredAttributes.reduce(
            (
                acc: { bookmarkedAttributes: AttributeValue[]; notBookmarkedAttributes: AttributeValue[] },
                attribute: LayerAttribute,
            ) => {
                const attributeValue = {
                    key: attribute.column,
                    order: attribute.order,
                    value: null,
                };

                if (attribute.isBookmarked) {
                    acc.bookmarkedAttributes.push(attributeValue);

                    return acc;
                }
                acc.notBookmarkedAttributes.push(attributeValue);

                return acc;
            },
            { bookmarkedAttributes: [], notBookmarkedAttributes: [] },
        );
    }

    private buildCreateEditEventAction(
        attributes: Record<string, unknown>,
        options: { depositId: string; geom?: unknown },
    ): EditEventAction | null {
        const entityId: string | null = this.getSnapshotEntityId();
        const layer: Layer | null = this.editEntityDetailService.layer();
        const layerId: string | null = this.getSnapshotLayerId();

        if (!entityId || !layer || !layerId) {
            return null;
        }

        const businessIdentifierKey = layer.businessIdentifier ?? 'uuid';
        const businessIdentifierValue =
            businessIdentifierKey === 'uuid' ? entityId : attributes?.[businessIdentifierKey];

        const after = {
            ...attributes,
            ...(options.geom ? { geom: options.geom } : {}), // The geom option must be set AFTER attributes
            businessIdentifier: businessIdentifierValue,
            deposit_id: options.depositId,
            uuid: entityId,
        };

        AssertService.isString(layer.tableName);

        return {
            action: EditAction.CREATE,
            after,
            before: null,
            businessIdentifier: { key: businessIdentifierKey, value: businessIdentifierValue },
            entityId,
            layerId,
            tableName: layer.tableName,
        };
    }

    private buildCreationContext(context: CreationContext): string | null {
        const { depositIds, entityId, layerIds, source } = context;

        if (!entityId || !layerIds.length) {
            return null;
        }

        if (this.entityDetailCreationContextService.isEditingExistingCreatedEntity()) {
            return `existing-created::${layerIds.join(',')}::${depositIds.join(',')}::${entityId}`;
        }

        if (!source) {
            return null;
        }

        return `${source}::${layerIds.join(',')}::${depositIds.join(',')}::${entityId}`;
    }

    private buildDepositOptions(
        deposits: DepositWithFolderAndStandard[],
    ): { label: string; value: DepositWithFolderAndStandard }[] {
        return deposits.map((deposit: DepositWithFolderAndStandard) => ({
            label: deposit.name,
            value: deposit,
        }));
    }

    private async closeEntityDetailPanel(): Promise<void> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        const urls = [RoutePathEnum.PROJECTS, projectId, RoutePathEnum.LAYERS];
        const sourceEntityId = this.mapCreationGeomService.sourceAttributes()?.['uuid'];
        const sourceLayerId = this.mapCreationGeomService.sourceLayerId();

        if (sourceEntityId && sourceLayerId) {
            AssertService.isString(sourceEntityId);
            urls.push(...[sourceLayerId, RoutePathEnum.ENTITY_DETAIL, sourceEntityId]);
        }

        const queryParams = this.homeService.isExternalPage()
            ? {
                  ext: true,
              }
            : undefined;

        this.navigationStateService.markNavigated();
        await this.router.navigate(urls, {
            queryParams,
        });

        this.entityDetailCreationContextService.resetPreservingAutoSaveState();
    }

    private closeMapCreationAfterSaveTrigger(shouldKeepAddEntityMode: boolean): void {
        this.mapCreationGeomService.updateCursorAfterCreation(shouldKeepAddEntityMode);
        this.mapCreationGeomService.resetCreationData();
        this.entityDetailCreationContextService.reset();
    }

    private closeTabularCreation(): void {
        this.projectEntityTableService.closeEntityDetailPanel();
        this.entityDetailCreationContextService.resetPreservingAutoSaveState();
    }

    private readonly currentCreationFinalizer = (): void => {
        this.handleFinalizeCurrentCreationNow();
    };

    private fetchDepositsFromLayers(layers: Layer[]): Promise<DepositWithFolderAndStandard[]> {
        const depositIds = new Set(layers.map((layer) => layer.deposit).filter(Boolean));

        return radashMap([...depositIds], (depositId) =>
            firstValueFrom(this.depositsService.depositControllerGetDepositWithFolderAndStandard({ id: depositId! })),
        );
    }

    private fetchLayersFromMap(): Promise<Layer[]> {
        return radashMap([...this.mapEditModeService.activeEditLayerIds()], (layerId) =>
            firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId })),
        );
    }

    private findLayerForDeposit(currentLayer: Layer, depositId: string): Layer | undefined {
        if (this.entityDetailCreationContextService.isMapCreation()) {
            return this.mapCreationGeomService.activeLayers.find(
                (layer: Layer) => layer.name === currentLayer.name && layer.deposit === depositId,
            );
        }

        const layersByName: LayerWithAttributes[] | undefined = this.projectEntityTableService
            .layersByName()
            ?.get(currentLayer.name);

        return (
            layersByName?.find((layer: LayerWithAttributes) => layer.deposit === depositId) ??
            (currentLayer.deposit === depositId ? currentLayer : undefined)
        );
    }

    private getCreationContext(): CreationContext {
        return {
            depositIds: this.entityDetailCreationContextService.depositIds(),
            entityId: this.entityDetailCreationContextService.entityId(),
            layerIds: this.entityDetailCreationContextService.layerIds(),
            source: this.entityDetailCreationContextService.source(),
        };
    }

    private getCurrentCreatedEntityProperties(): Record<string, unknown> | null {
        const projectId: string | undefined = this.projectBloc.value()?.id;
        const layerIds: string[] = this.entityDetailCreationContextService.layerIds();
        const entityId: string | null = this.entityDetailCreationContextService.entityId();

        if (!projectId || !layerIds || !entityId) {
            return null;
        }

        const layerState: EntityState[] = this.editModeStateService.getLayerState(projectId, layerIds[0]);
        const entityEdit: EntityState | undefined = layerState.find((currentEdit): boolean => {
            return currentEdit.entityId === entityId && currentEdit.action === EditAction.CREATE;
        });

        return entityEdit?.properties ?? null;
    }

    private getExistingEntityDepositId(): string | null {
        const properties: Record<string, unknown> | null = this.getCurrentCreatedEntityProperties();

        return (properties?.['deposit_id'] as string | undefined) ?? null;
    }

    private getFormAttributes(): Record<string, unknown> {
        const bookmarked = this.editEntityDetailService.bookmarkedAttributesForm();
        const notBookmarked = this.editEntityDetailService.notBookmarkedAttributesForm();

        if (!bookmarked || !notBookmarked) {
            return {};
        }

        return {
            ...bookmarked.value,
            ...notBookmarked.value,
        };
    }

    private getLayerFromTable(layerId: string): Layer | null {
        return (
            this.projectEntityTableService.layers().find((layer: LayerWithAttributes) => layer.id === layerId) ?? null
        );
    }

    private getMapEntityEditAction(attributes: Record<string, unknown>): EditEventAction | null {
        const layer = this.mapCreationGeomService.selectedLayer();
        if (!layer?.deposit) {
            return null;
        }

        const geom = this.editEntityDetailService.after.get('geom') ?? this.mapCreationGeomService.getCreatedGeom();

        return this.buildCreateEditEventAction(attributes, {
            depositId: layer.deposit,
            geom,
        });
    }

    private getSnapshotEntityId(): string | null {
        return this.creationContextSnapshot.entityId;
    }

    private getSnapshotLayerId(): string | null {
        return this.creationContextSnapshot.layerId;
    }

    private getTabularEntityEditAction(attributes: Record<string, unknown>): EditEventAction | null {
        const layer: Layer | null = this.editEntityDetailService.layer();
        const selectedDeposit: DepositWithFolderAndStandard | null = this.mapCreationGeomService.selectedDeposit();

        if (!layer || !selectedDeposit) {
            return null;
        }

        return this.buildCreateEditEventAction(attributes, {
            depositId: selectedDeposit.id,
        });
    }

    private handleCreationContextRefresh(context: CreationContext): void {
        const currentContextKey: string | null = this.buildCreationContext(context);

        if (!currentContextKey || currentContextKey === this.lastHandledCreationContextKey) {
            return;
        }

        this.lastHandledCreationContextKey = currentContextKey;
        this.hasSaved.set(false);

        this.editEntityDetailService.resetFormsValues();
        this.mapCreationGeomService.selectedDeposit.set(null);
        this.synchronizeCreationContextSnapshot(context);

        if (this.entityDetailCreationContextService.isMapCreation()) {
            void this.initializeFromMapContext();

            return;
        }

        void this.initializeFromTabularContext();
    }

    private handleFinalizeCurrentCreationNow(): void {
        if (!this.canSaveCurrentCreation()) {
            this.editEntityDetailService.resetRecomputeError();
            this.mapCreationGeomService.selectedDeposit.set(null);

            return;
        }

        void this.validateEntity(true);

        this.editEntityDetailService.resetRecomputeError();
        this.mapCreationGeomService.selectedDeposit.set(null);
    }

    private hasDirtyForms(): boolean {
        return (
            !!this.editEntityDetailService.bookmarkedAttributesForm()?.dirty ||
            !!this.editEntityDetailService.notBookmarkedAttributesForm()?.dirty
        );
    }

    private hasValidForms(): boolean {
        return !!(
            this.editEntityDetailService.bookmarkedAttributesForm() &&
            this.editEntityDetailService.notBookmarkedAttributesForm()
        );
    }

    private async initializeFromMapContext(): Promise<void> {
        const layers = await this.fetchLayersFromMap();
        if (!layers.length) {
            return;
        }

        this.mapCreationGeomService.activeLayers = layers;

        const deposits = await this.fetchDepositsFromLayers(layers);
        const options = this.buildDepositOptions(deposits);
        this.editEntityDetailService.layer.set(this.mapCreationGeomService.selectedLayer());

        this.setDepositState(options);
    }

    private async initializeFromTabularContext(): Promise<void> {
        const layerIds: string[] = this.entityDetailCreationContextService.layerIds();
        if (!layerIds.length) {
            return;
        }

        const currentLayer: Layer | null = this.getLayerFromTable(layerIds[0]);
        if (!currentLayer) {
            return;
        }

        const layersByName: LayerWithAttributes[] | undefined = this.projectEntityTableService
            .layersByName()
            ?.get(currentLayer.name);

        const layers: Layer[] = layersByName?.length ? layersByName : [currentLayer];

        const deposits: DepositWithFolderAndStandard[] = await this.fetchDepositsFromLayers(layers);

        this.editEntityDetailService.setBusinessIdentifierInfo();

        const options = this.buildDepositOptions(deposits);

        this.setDepositState(options);
    }

    private isSnapshotMapCreation(): boolean {
        return this.creationContextSnapshot.source === CreationSourceEnum.MAP;
    }

    private restoreCreatedEntityFormValues(): void {
        const properties = this.getCurrentCreatedEntityProperties();
        if (!properties) {
            return;
        }

        this.editEntityDetailService.bookmarkedAttributesForm()?.patchValue(properties);
        this.editEntityDetailService.notBookmarkedAttributesForm()?.patchValue(properties);

        this.editEntityDetailService.markAttributeFormsAsPristine();
    }

    private setDepositState(options: { label: string; value: DepositWithFolderAndStandard }[]): void {
        this.depositOptions.set(options);

        if (!this.mapCreationGeomService.selectedDeposit() && options.length) {
            const existingDepositId: string | null = this.getExistingEntityDepositId();

            let matchedOption: DepositWithFolderAndStandard = options[0].value;

            if (existingDepositId) {
                matchedOption =
                    options.find((option) => option.value.id === existingDepositId)?.value ?? options[0].value;
            }

            this.mapCreationGeomService.selectedDeposit.set(matchedOption);
        }

        const selected: DepositWithFolderAndStandard | null = this.mapCreationGeomService.selectedDeposit();

        if (!selected) {
            return;
        }

        const defaultOptions = options.filter((option) => option.value.id === selected.id);

        this.defaultOptions.set(defaultOptions);
    }

    private async setLayerAttributes(layerId: string): Promise<void> {
        try {
            const attributes = await firstValueFrom(
                this.layersService.layerControllerGetAttributes({
                    id: layerId,
                }),
            );

            this.mapCreationGeomService.layerAttributes = attributes.filter(
                (attribute: LayerAttribute) => !excludedAttributes.includes(attribute.column),
            );
        } catch {
            this.snackBarService.openErrorSnackBar('errorDefault');
            this.isLoading.set(false);

            return;
        }
    }

    private async setLayerDataAttributes(entityId: string): Promise<void> {
        const layer: Layer | null = this.getCurrentLayer();
        if (!layer) {
            return;
        }

        this.isLoading.set(true);

        if (!this.mapCreationGeomService.layerAttributes.length) {
            await this.setLayerAttributes(layer.id);
        }

        const { bookmarkedAttributes, notBookmarkedAttributes } = this.buildAttributeFormState(
            this.mapCreationGeomService.layerAttributes,
        );

        this.setAttributes(bookmarkedAttributes, notBookmarkedAttributes);

        this.editEntityDetailService.layer.set(layer);
        this.editEntityDetailService.entityId.set(entityId);

        if (!this.mapCreationGeomService.attributeInformationList.length) {
            this.mapCreationGeomService.attributeInformationList = await this.attributeInformationService.loadForLayer(
                layer.id,
            );
        }

        await this.editEntityDetailService.initEditForm(
            this.bookmarkedAttributes(),
            this.notBookmarkedAttributes(),
            this.mapCreationGeomService.attributeInformationList,
        );

        this.restoreCreatedEntityFormValues();
        this.editEntityDetailService.setBusinessIdentifierInfo();

        this.isLoading.set(false);
    }

    private synchronizeCreationContextSnapshot(context: CreationContext): void {
        this.creationContextSnapshot = {
            depositIds: context.depositIds,
            entityId: context.entityId,
            layerId: context.layerIds[0] ?? null,
            source: context.source,
        };
    }
}
